import React, { useEffect, useState } from "react";
import { AddIcon, LoadingIcon } from "../../assets/icons/Icons";
import FormSection from "../form/Section";
import FormHeader from "../form/Header";
import Switch from "../form/Switch";
import Popup from "../Popup";
import { CreateRole } from "../../api/roles/createRole";
import { ToggleRoleActive } from "../../api/roles/toggleRoleActive";
import { GetPermissions } from "../../api/roles/getPermissions";
import PermissionMatrix from "./PermissionMatrix";
import {
  buildPermissionGroups,
  serializePermissions,
  hasFeaturePermission,
  isReservedRoleName,
  OFFICE_ACCESS_KEY,
} from "./permissionCatalog";
import { useTranslation } from "react-i18next";

const CreateRoleForm = ({ onCreate, onClose }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);

  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState({});
  const [officeAccessAvailable, setOfficeAccessAvailable] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const response = await GetPermissions();
        const catalogMap = response.permissions || {};
        setGroups(buildPermissionGroups(catalogMap));
        setOfficeAccessAvailable(!!catalogMap[OFFICE_ACCESS_KEY]);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingCatalog(false);
      }
    };
    loadCatalog();
  }, []);

  // Office scope is a role-wide flag, not a feature: on = all offices, off =
  // limited to the offices assigned to each user. Wired to the all_offices
  // grant so it serializes alongside the matrix permissions.
  const officeAccessAll = !!selected[OFFICE_ACCESS_KEY]?.read;
  const setOfficeAccessAll = (checked) =>
    setSelected({
      ...selected,
      [OFFICE_ACCESS_KEY]: { read: checked, write: false },
    });

  // A role must have a name and grant at least one feature permission; the
  // office-scope flag alone does not make a usable role.
  const featureSelected = hasFeaturePermission(selected);
  const nameReserved = isReservedRoleName(name);

  useEffect(() => {
    setSubmitDisabled(!name.trim() || !featureSelected || nameReserved);
  }, [name, featureSelected, nameReserved]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitDisabled(true);
    setError("");
    try {
      const created = await CreateRole({
        name,
        description,
        permissions: serializePermissions(selected),
      });
      // New roles are created active by default; toggle if the admin turned it off.
      if (!active && created?.id) {
        await ToggleRoleActive(created.id);
      }
      setLoading(false);
      onCreate();
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setSubmitDisabled(false);
    }
  };

  return (
    <Popup size="large" withClose withOverlay onClose={onClose}>
      <form method="post" onSubmit={handleSubmit}>
        <FormHeader
          title={t("Create Role")}
          description={t(
            "Define a role and choose exactly which permissions it grants."
          )}
          className="mb-5"
        />

        <div className="row mb-5">
          <div className="col-xs-6">
            <FormSection title={t("Role Name")} required>
              <input
                type="text"
                name="name"
                id="name"
                maxLength="255"
                placeholder={t("Role name")}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {nameReserved && (
                <p className="color-error mt-2">
                  {t(
                    "This name is reserved for a built-in role. Choose a different name."
                  )}
                </p>
              )}
            </FormSection>
          </div>
          <div className="col-xs-6">
            <FormSection title={t("Active")}>
              <Switch title={t("Active")}>
                <input
                  type="checkbox"
                  name="active"
                  id="active"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
              </Switch>
            </FormSection>
          </div>
        </div>

        <div className="row mb-5">
          <div className="col-xs-12">
            <FormSection title={t("Description")}>
              <input
                type="text"
                name="description"
                id="description"
                maxLength="500"
                placeholder={t("Short description of this role")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FormSection>
          </div>
        </div>

        {officeAccessAvailable && (
          <div className="row mb-5">
            <div className="col-xs-12">
              <FormSection title={t("Office Access")}>
                <Switch title={t("This role can access all offices")}>
                  <input
                    type="checkbox"
                    name="all_offices"
                    id="all_offices"
                    checked={officeAccessAll}
                    onChange={(e) => setOfficeAccessAll(e.target.checked)}
                  />
                </Switch>
                <p
                  className="text-muted mt-2"
                  style={{ fontSize: "0.85rem" }}
                >
                  {t(
                    "When off, users with this role are limited to the offices assigned to them."
                  )}
                </p>
              </FormSection>
            </div>
          </div>
        )}

        <div className="row mb-5">
          <div className="col-xs-12">
            <FormSection title={t("Permissions")}>
              {loadingCatalog ? (
                <p>{t("Loading permissions...")}</p>
              ) : (
                <PermissionMatrix
                  groups={groups}
                  selected={selected}
                  onChange={setSelected}
                  disabled={loading}
                />
              )}
              {!loadingCatalog && !featureSelected && (
                <p className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
                  {t("Select at least one permission to save this role.")}
                </p>
              )}
            </FormSection>
          </div>
        </div>

        <div className="text-right">
          <button
            className={`btn btn-primary btn-medium ${loading ? "btn-loading" : ""}`}
            type="submit"
            name="submit"
            disabled={submitDisabled}
          >
            {loading ? <LoadingIcon /> : <AddIcon />}
            {t("Create Role")}
          </button>
        </div>
        {error && <p className="color-error text-center mt-3">{error}</p>}
      </form>
    </Popup>
  );
};

export default CreateRoleForm;
